import styles from "./Propietario.module.css";
import stepsStyles from "../steps.module.css";
import Modal from "../../../Modals/ModalOwner";
import { useState, useEffect } from "react";
import { FieldError, CedulaStatus, ForeignToggle, SectionLabel } from "../../../FieldHelpers";
import { useCedulaLookup } from "../../../../services/useCedulaLookup.js";
import { useEsCliente } from "../../../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../../../shared/i18n/useLang";

export default function Propietario({ formData, setFormData, errors = {}, section = "all" }) {
  const esCliente = useEsCliente();
  const L = useLang();
  const [emails, setEmails] = useState(formData.propietario?.emailExtras || []);

  function handleAddEmail(e) {
    e.preventDefault();
    const newEmail = { id: Date.now(), email: "" };
    setEmails((prev) => [...prev, newEmail]);
    setFormData((prev) => ({
      ...prev,
      propietario: {
        ...prev.propietario,
        emailExtras: [...(prev.propietario?.emailExtras || []), newEmail],
      },
    }));
  }

  function handleEmailChange(id, value) {
    const updated = emails.map((item) =>
      item.id === id ? { ...item, email: value } : item,
    );
    setEmails(updated);
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, emailExtras: updated },
    }));
  }

  function handleDeleteEmail(id) {
    const updated = emails.filter((item) => item.id !== id);
    setEmails(updated);
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, emailExtras: updated },
    }));
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, [name]: value },
    }));
  }

  const showIdentidad = section === "all" || section === "identidad";
  const showContacto  = section === "all" || section === "contacto";
  const showEmails    = section === "all" || section === "contacto";
  const showAddress   = esCliente && (section === "all" || section === "identidad");
  const showCoOwners  = !esCliente && section === "all";

  const titulo = esCliente
    ? (section === "contacto"
        ? L("Contacto del Cliente", "Client Contact")
        : L("Identificación del Cliente", "Client Identification"))
    : L("Información del propietario", "Owner Information");

  return (
    <div className="step">
        <h3 className="p-0 mt-2 mb-0 title" style={{fontSize:"1.3rem"}}>{titulo}</h3>
        <p className={`${stepsStyles["inactive"]} mb-3` } style={{fontSize:"1rem"}}>
          {L("Inserte los siguientes detalles para continuar.", "Enter the following details to continue.")}
        </p>

      <div className={stepsStyles["form-wrapper"]}>
        {showIdentidad && (
          <>
            <SectionLabel icon="fa-id-card">{L("Identidad", "Identity")}</SectionLabel>
            <OwnerIdentity
              formData={formData}
              handleChange={handleChange}
              setFormData={setFormData}
              errors={errors}
              esCliente={esCliente}
            />
          </>
        )}

        {showAddress && (
          <>
            <SectionLabel icon="fa-location-dot">{L("Dirección", "Address")}</SectionLabel>
            <OwnerAddress formData={formData} setFormData={setFormData} />
          </>
        )}

        {showContacto && (
          <>
            <SectionLabel icon="fa-phone">{L("Contacto", "Contact")}</SectionLabel>
            <OwnerContact
              formData={formData}
              handleChange={handleChange}
              setFormData={setFormData}
              errors={errors}
              esCliente={esCliente}
            />
          </>
        )}

        {showEmails && (
          <>
            <SectionLabel icon="fa-envelope">{L("Correos electrónicos", "Emails")}</SectionLabel>
            <OwnerEmails>
              <EmailPrincipal
                formData={formData}
                handleChange={handleChange}
                handleAddEmail={handleAddEmail}
                errors={errors}
              />
              {emails.map((item, idx) => (
                <EmailExtra
                  key={item.id}
                  id={item.id}
                  email={item.email}
                  handleEmailChange={handleEmailChange}
                  handleDeleteEmail={handleDeleteEmail}
                  error={errors.emailExtras?.[idx]}
                />
              ))}
            </OwnerEmails>
          </>
        )}

        {showCoOwners && <CoOwnerSection formData={formData} setFormData={setFormData} />}
      </div>
    </div>
  );
}

// ── Identidad con búsqueda de cédula ────────────────────────────────────────
function OwnerIdentity({ formData, handleChange, setFormData, errors, esCliente = false }) {
  const { buscar, resultado, estado, reset } = useCedulaLookup();
  const [autoloaded, setAutoloaded] = useState(false);
  const esJuridica = formData.propietario.tipo_cedula === "JURIDICA";
  const L = useLang();

  // Aplicar datos del TSE/local cuando llegan
  useEffect(() => {
    if (resultado && !autoloaded) {
      setFormData((prev) => ({
        ...prev,
        propietario: {
          ...prev.propietario,
          nombre: resultado.nombre || prev.propietario.nombre,
          apellidos: resultado.apellidos || prev.propietario.apellidos,
        },
      }));
      setAutoloaded(true);
    }
  }, [resultado, autoloaded, setFormData]);

  function handleCedulaChange(e) {
    handleChange(e);
    setAutoloaded(false);
    reset();
  }

  function handleCedulaBlur(e) {
    buscar(e.target.value);
  }

  return (
    <>
      {/* Tipo de cédula — solo en modo cliente */}
      {esCliente && (
        <div className="form-group">
          <label className="form-label">{L("Tipo de identificación", "ID type")}</label>
          <select
            name="tipo_cedula"
            className={`${stepsStyles["form-select"]} form-select ${stepsStyles["form-control"]} form-control`}
            value={formData.propietario.tipo_cedula || ""}
            onChange={handleChange}
          >
            <option value="">{L("— Seleccioná el tipo —", "— Select type —")}</option>
            <option value="FISICA">{L("Física", "Individual")}</option>
            <option value="JURIDICA">{L("Jurídica", "Company")}</option>
            <option value="DIMEX">DIMEX</option>
            <option value="NITE">NITE</option>
            <option value="PASAPORTE">{L("Pasaporte / Otro", "Passport / Other")}</option>
          </select>
        </div>
      )}

      {/* Cédula */}
      <div className="form-group">
        <label className="form-label">
          {L("Cédula", "ID number")} <span style={{ color: "#f87171" }}>*</span>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <input
              name="cedula"
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              style={{ borderColor: errors.cedula ? "#ef4444" : undefined }}
              placeholder="Ej: 123456789"
              value={formData.propietario.cedula}
              onChange={handleCedulaChange}
              onBlur={handleCedulaBlur}
            />
          </div>
          <button
            type="button"
            onClick={() => buscar(formData.propietario.cedula)}
            disabled={estado === "buscando"}
            title="Buscar datos en el TSE"
            style={{
              padding: "0 12px",
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.4)",
              borderRadius: 6,
              color: "#f97316",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontSize: "0.78rem",
            }}
          >
            {estado === "buscando" ? (
              <i className="fa-solid fa-circle-notch fa-spin" />
            ) : (
              <>
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{ marginRight: 4 }}
                />
                {L("Buscar", "Search")}
              </>
            )}
          </button>
        </div>
        <CedulaStatus estado={estado} />
        <FieldError msg={errors.cedula} />
      </div>

      {/* Persona jurídica (modo cliente): solo razón social, sin apellidos */}
      {esCliente && esJuridica ? (
        <div className="form-group">
          <label className="form-label">
            {L("Nombre / Razón social", "Name / Company")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            name="nombre_sociedad"
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
            placeholder="Ej: Inversiones Sol S.A."
            value={formData.propietario.nombre_sociedad || ""}
            onChange={handleChange}
          />
          <FieldError msg={errors.nombre} />
        </div>
      ) : (
        <>
          {/* Nombre y Apellidos */}
          <div className="form-group row g-3 g-md-4">
            <div className="col-12 col-md-6">
              <label className="form-label">
                {L("Nombre", "First name")} <span style={{ color: "#f87171" }}>*</span>
              </label>
              <input
                name="nombre"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{ borderColor: errors.nombre ? "#ef4444" : undefined }}
                placeholder={L("Digite el nombre", "Enter the name")}
                value={formData.propietario.nombre}
                onChange={handleChange}
              />
              <FieldError msg={errors.nombre} />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">
                {L("Apellidos", "Last name")} <span style={{ color: "#f87171" }}>*</span>
              </label>
              <input
                name="apellidos"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{ borderColor: errors.apellidos ? "#ef4444" : undefined }}
                placeholder="Digite los apellidos"
                value={formData.propietario.apellidos}
                onChange={handleChange}
              />
              <FieldError msg={errors.apellidos} />
            </div>
          </div>

          {/* Nombre Sociedad — solo en modo condominio (en cliente lo cubre el tipo jurídica) */}
          {!esCliente && (
            <div className="form-group mt-3">
              <label className="form-label">
                Nombre de Sociedad
                <span style={{fontSize:"0.75rem",color:"#94a3b8",marginLeft:8}}>
                  (si es persona jurídica — dejar Nombre/Apellidos vacíos)
                </span>
              </label>
              <input
                name="nombre_sociedad"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                placeholder="Ej: Inversiones Sol S.A."
                value={formData.propietario.nombre_sociedad || ""}
                onChange={handleChange}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Dirección desglosada (modo cliente) ──────────────────────────────────────
function OwnerAddress({ formData, setFormData }) {
  const L = useLang();
  const chg = (field) => (e) =>
    setFormData((prev) => ({
      ...prev,
      propietario: { ...prev.propietario, [field]: e.target.value },
    }));

  return (
    <>
      <div className="form-group row g-3 g-md-4 mt-1">
        <div className="col-12 col-md-4">
          <label className="form-label">{L("Provincia", "Province")}</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: San José"
            value={formData.propietario.direccion_provincia || ""}
            onChange={chg("direccion_provincia")}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label">{L("Cantón", "Canton")}</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: Central"
            value={formData.propietario.direccion_canton || ""}
            onChange={chg("direccion_canton")}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label">{L("Distrito", "District")}</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: Carmen"
            value={formData.propietario.direccion_distrito || ""}
            onChange={chg("direccion_distrito")}
          />
        </div>
      </div>
      <div className="form-group mt-1">
        <label className="form-label">{L("Otras señas", "Other details")}</label>
        <textarea
          rows={2}
          className={`${stepsStyles["form-control"]} form-control`}
          placeholder={L("Detalle de la dirección…", "Address details…")}
          value={formData.propietario.direccion_senas || ""}
          onChange={chg("direccion_senas")}
          style={{ resize: "none" }}
        />
      </div>
    </>
  );
}

// ── Teléfonos y Placa ─────────────────────────────────────────────────────────
function OwnerContact({
  formData,
  handleChange,
  setFormData,
  errors,
  esCliente = false,
}) {
  const L = useLang();
  const chg = (field) => (e) =>
    setFormData(prev => ({
      ...prev,
      propietario: { ...prev.propietario, [field]: e.target.value },
    }));

  return (
    <>
      <div className="form-group row g-3 g-md-4 mt-1">
        <div className="col-12 col-md-4">
          <label className="form-label">{L("Teléfono Casa", "Home Phone")}</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: 2222-1234"
            value={formData.propietario.telefono_casa || ""}
            onChange={chg("telefono_casa")}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label">{L("Teléfono Celular", "Mobile Phone")}</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: 8888-1234"
            value={formData.propietario.telefono_cel || ""}
            onChange={chg("telefono_cel")}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label">{esCliente ? L("Otro teléfono", "Other phone") : L("Teléfono Trabajo", "Work Phone")}</label>
          <input
            type="text"
            className={`${stepsStyles["form-control"]} form-control`}
            placeholder="Ej: 2200-1234"
            value={formData.propietario.telefono_trabajo || ""}
            onChange={chg("telefono_trabajo")}
          />
        </div>
      </div>

      {/* Placa / Notas — no aplican en modo cliente */}
      {!esCliente && (
        <div className="form-group row g-3 g-md-4 mt-1">
          <div className="col-12 col-md-6">
            <label className="form-label">{L("Placa de vehículo", "License plate")}</label>
            <input
              name="placaVehiculo"
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              placeholder={formData.propietario.placaExtranjera ? "Placa extranjera" : "Ej: ABC-123"}
              value={formData.propietario.placaVehiculo}
              onChange={handleChange}
            />
            <ForeignToggle
              checked={formData.propietario.placaExtranjera}
              onChange={() =>
                setFormData(prev => ({
                  ...prev,
                  propietario: { ...prev.propietario, placaExtranjera: !prev.propietario.placaExtranjera },
                }))
              }
              label="Placa extranjera"
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">{L("Notas", "Notes")}</label>
            <textarea
              rows={2}
              className={`${stepsStyles["form-control"]} form-control`}
              placeholder="Observaciones opcionales..."
              value={formData.propietario.notas || ""}
              onChange={chg("notas")}
              style={{resize:"none"}}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ── Email principal ──────────────────────────────────────────────────────────
function OwnerEmails({ children }) {
  const L = useLang();
  return (
    <div
      className={`form-group`}
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
    >
      <label className="form-label m-0">
        {L("Email principal", "Primary email")} <span style={{ color: "#f87171" }}>*</span>
        <span style={{ fontSize: "0.74rem", color: "var(--text-muted)", fontWeight: 400, marginLeft: 8 }}>
          {L("usá el botón + para agregar correos adicionales", "use the + button to add more emails")}
        </span>
      </label>
      {children}
    </div>
  );
}

function EmailPrincipal({ formData, handleChange, handleAddEmail, errors }) {
  return (
    <div>
      <div className="input-group">
        <input
          name="emailPrincipal"
          type="email"
          className={`${stepsStyles["form-control"]} form-control`}
          style={{ borderColor: errors.emailPrincipal ? "#ef4444" : undefined }}
          placeholder="email@ejemplo.com"
          value={formData.propietario.emailPrincipal}
          onChange={handleChange}
        />
        <button
          className={`btn`}
          style={{
            background: "rgba(249,115,22,0.15)",
            border: "1px solid rgba(249,115,22,0.4)",
            color: "#f97316",
          }}
          onClick={handleAddEmail}
          title="Agregar email adicional"
        >
          <i className="fa-solid fa-plus" />
        </button>
      </div>
      <FieldError msg={errors.emailPrincipal} />
    </div>
  );
}

function EmailExtra({
  id,
  email,
  handleEmailChange,
  handleDeleteEmail,
  error,
}) {
  return (
    <div>
      <div className="input-group">
        <input
          type="email"
          className={`${stepsStyles["form-control"]} form-control`}
          style={{ borderColor: error ? "#ef4444" : undefined }}
          placeholder="email@ejemplo.com"
          value={email}
          onChange={(e) => handleEmailChange(id, e.target.value)}
        />
        <button
          className="btn"
          style={{
            background: "rgba(248,113,113,0.1)",
            border: "1px solid rgba(248,113,113,0.3)",
            color: "#f87171",
          }}
          onClick={() => handleDeleteEmail(id)}
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>
      <FieldError msg={error} />
    </div>
  );
}

// ── Copropietarios ───────────────────────────────────────────────────────────
function CoOwnerSection({ formData, setFormData }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedIndex, setSelectedIndex] = useState(null);

  function openAdd(e) {
    e.preventDefault();
    setMode("add");
    setSelectedIndex(null);
    setOpen(true);
  }
  function openEdit(index) {
    setMode("edit");
    setSelectedIndex(index);
    setOpen(true);
  }
  function handleDelete(id) {
    setFormData((prev) => ({
      ...prev,
      copropietarios: prev.copropietarios.filter((o) => o.id !== id),
    }));
  }

  return (
    <>
      <SectionLabel icon="fa-users">Copropietarios</SectionLabel>
      <div className={`form-group ${styles["form-group-coop"]}`} style={{ marginTop: 0 }}>
        <p className={`${stepsStyles["inactive"]} m-0`} style={{ fontSize: "0.88rem" }}>
          Agregá copropietarios si es necesario{" "}
          <span style={{ color: "var(--text-secondary)", fontSize: "0.78rem" }}>
            (opcional)
          </span>
        </p>
        <button className={`${styles.btn}`} onClick={openAdd}>
          <i className="fa-solid fa-plus" style={{ marginRight: 6 }} />
          Agregar Copropietario
        </button>
      </div>

      <div className="listCoOwner d-flex flex-column" style={{ gap: "6px" }}>
        {formData.copropietarios.map((co) => (
          <CoOwner
            key={co.id}
            coOwner={co}
            onDelete={handleDelete}
            openEdit={openEdit}
          />
        ))}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        mode={mode}
        selectedIndex={selectedIndex}
      />
    </>
  );
}

function CoOwner({ coOwner, onDelete, openEdit }) {
  // Para sociedades: nombre_sociedad; para personas: nombre + apellidos
  const nombreMostrado = coOwner.nombre_sociedad
    || `${coOwner.nombre || ""} ${coOwner.apellidos || ""}`.trim()
    || coOwner.cedula
    || "—";

  // Teléfono: preferir cel → casa → trabajo
  const tel = coOwner.telefono_cel || coOwner.telefono_casa || coOwner.telefono_trabajo || coOwner.telefono;

  return (
    <div className={styles.infoCoOwner}>
      <div className={styles.iconCoOwner}>
        <i className={`fa-solid ${coOwner.nombre_sociedad ? "fa-building" : "fa-user"}`} />
      </div>
      <div className={styles.identityCoOwner}>
        <h2 style={{ fontSize: "20px", margin: 0 }}>
          {nombreMostrado}
          {coOwner.cedula && nombreMostrado !== coOwner.cedula && (
            <span
              style={{ color: "var(--text-secondary)", fontWeight: 400, fontSize: "14px" }}
            >
              {" "}
              ({coOwner.cedula})
            </span>
          )}
        </h2>
        <p style={{ fontSize: "14px", margin: 0, color: "var(--text-muted)" }}>
          {tel && `📞 ${tel}`}
          {tel && coOwner.placaVehiculo && " · "}
          {coOwner.placaVehiculo && `🚗 ${coOwner.placaVehiculo}`}
        </p>
      </div>
      <div className={styles.actionsCoOwner}>
        <button
          className={styles["icon-btn"]}
          type="button"
          onClick={() => openEdit(coOwner.id)}
        >
          <i className="fa-solid fa-square-pen" />
        </button>
        <button
          className={styles["icon-btn"]}
          type="button"
          onClick={() => onDelete(coOwner.id)}
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>
    </div>
  );
}
